import { Box, IconButton } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import { Calendar } from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { maskTypedDate } from './dateMask';
import {
  formatIsoDate,
  isWithinBounds,
  parseCompleteTypedDate,
  parseTypedDate,
  type IsoDate,
} from './isoDate';

export type FieldSize = 'medium' | 'small';

/** Where the calendar glyph sits: trailing (the Figma `input` default) or leading. */
export type IconPlacement = 'start' | 'end';

/**
 * `outlined` (default) — the bordered input. `text` — no border or fill at rest, the
 * value reading as plain text in a properties list; hover washes it and focus draws the
 * brand border, as the outlined field does.
 */
export type FieldVariant = 'outlined' | 'text';

interface ShellStyleProps {
  size: FieldSize;
  variant?: FieldVariant;
  /** The side the toggle is on — the tighter inset goes there. */
  iconPlacement?: IconPlacement;
  active?: boolean;
  error?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  fullWidth?: boolean;
}

const SHELL_PROPS = new Set([
  'size',
  'variant',
  'iconPlacement',
  'active',
  'error',
  'disabled',
  'readOnly',
  'fullWidth',
]);

/** Inset per size: the glyph's side is tighter, because the toggle carries its own padding. */
const SHELL_PADDING: Record<FieldSize, Record<IconPlacement, string>> = {
  medium: { start: '0 12px 0 6px', end: '0 6px 0 12px' },
  small: { start: '0 8px 0 4px', end: '0 4px 0 8px' },
};

/**
 * The box every date field sits in, in the states every other input in the app takes
 * (`MuiOutlinedInput`), so a row of mixed fields reads as one: 40 high and r8 (`small`:
 * 24 high, r4, 12px type), the `border.main` outline, the `grey[120]` wash on hover, the
 * brand border with its 1px glow while open or focused, the red rule on the red wash in
 * error, and the theme's inert fills for disabled and read-only.
 */
const ShellBox = styled(Box, {
  shouldForwardProp: (prop) => !SHELL_PROPS.has(String(prop)),
})<ShellStyleProps>(({
  theme,
  size,
  variant = 'outlined',
  iconPlacement = 'end',
  active,
  error,
  disabled,
  readOnly,
  fullWidth,
}) => {
  const small = size === 'small';
  // As `MuiOutlinedInput.Mui-focused`; focus outranks the hover wash.
  const focused = error
    ? { borderColor: theme.palette.error.main }
    : {
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 1px ${theme.palette.primary.lighter}`,
      };
  return {
    display: fullWidth ? 'flex' : 'inline-flex',
    width: fullWidth ? '100%' : undefined,
    alignItems: 'center',
    gap: small ? 4 : 8,
    boxSizing: 'border-box',
    height: small ? 24 : 40,
    padding: SHELL_PADDING[size][iconPlacement],
    borderRadius: small ? 4 : 8,
    border: `1px solid ${error ? theme.palette.error.main : theme.palette.grey[90]}`,
    backgroundColor: error ? theme.palette.error.light : theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontSize: small ? 12 : 14,
    fontWeight: 500,
    // Raleway's default figures are old-style: a date has to read like the number in
    // any other input beside it (Figma's input text sets lining, proportional).
    fontVariantNumeric: 'lining-nums proportional-nums',
    cursor: disabled || readOnly ? 'default' : 'text',
    transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow'], {
      duration: theme.transitions.duration.shortest,
    }),
    ...(!disabled &&
      !readOnly && {
        // An error keeps its wash under the pointer: the red is the message.
        '&:hover': { backgroundColor: error ? theme.palette.error.light : theme.palette.grey[120] },
        '&:focus-within': focused,
      }),
    // A bare value at rest; the hover restates the whole wash (a nested key replaces).
    ...(variant === 'text' &&
      !error && {
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        ...(!disabled &&
          !readOnly && {
            '&:hover': {
              borderColor: theme.palette.divider,
              backgroundColor: theme.palette.grey[120],
            },
          }),
      }),
    ...(active && focused),
    ...(disabled && {
      borderColor: 'transparent',
      boxShadow: 'none',
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.text.disabled,
    }),
    ...(readOnly && {
      borderColor: 'transparent',
      boxShadow: 'none',
      backgroundColor: theme.palette.grey[120],
    }),
    // A text field that cannot be edited is just its value.
    ...(variant === 'text' &&
      (disabled || readOnly) && {
        backgroundColor: 'transparent',
        color: theme.palette.text.primary,
      }),
  };
});

export interface FieldShellProps extends Omit<
  ComponentPropsWithoutRef<typeof ShellBox>,
  'iconPlacement'
> {
  /** The calendar toggle — laid before or after the content, as `iconPlacement` says. */
  toggle?: ReactNode;
  /** Trailing (default) or leading. Without a toggle, the shell keeps the trailing inset. */
  iconPlacement?: IconPlacement;
}

/** The shell with its calendar toggle in place, so every field lays the glyph out one way. */
export const FieldShell = forwardRef<HTMLDivElement, FieldShellProps>(function FieldShell(
  { toggle, iconPlacement = 'end', children, ...shell },
  ref,
) {
  const leads = Boolean(toggle) && iconPlacement === 'start';
  return (
    <ShellBox ref={ref} iconPlacement={leads ? 'start' : 'end'} {...shell}>
      {leads && toggle}
      {children}
      {!leads && toggle}
    </ShellBox>
  );
});

const SegmentInputElement = styled('input', {
  shouldForwardProp: (prop) => prop !== 'grow' && prop !== 'fieldSize',
})<{ grow?: boolean; fieldSize: FieldSize }>(({ theme, grow, fieldSize }) => ({
  // Wide enough for `DD.MM.YYYY` at the field's type size, never wider unless asked.
  width: fieldSize === 'small' ? 72 : 84,
  flex: grow ? '1 1 auto' : '0 0 auto',
  minWidth: 0,
  margin: 0,
  padding: 0,
  border: 0,
  outline: 0,
  background: 'transparent',
  font: 'inherit',
  // Set again AFTER the shorthand, which would otherwise reset it to the font's default.
  fontVariantNumeric: 'lining-nums proportional-nums',
  color: 'inherit',
  // The theme's input placeholder (`MuiOutlinedInput`).
  '&::placeholder': {
    color: alpha(theme.palette.text.secondary, 0.5),
    opacity: 1,
  },
  '&[aria-invalid="true"]': {
    color: theme.palette.error.main,
  },
  '&:disabled': { cursor: 'default' },
  // As wide as its text where the browser can size it so: a range then reads "date – date"
  // with no gap before the dash. Elsewhere the fixed width above stands.
  '@supports (field-sizing: content)': grow ? {} : { fieldSizing: 'content', width: 'auto' },
}));

/** Why typed text was refused: not a day at all, or a day outside the bounds. */
export type TypedProblem =
  | { kind: 'invalid' }
  | { kind: 'before'; limit: IsoDate }
  | { kind: 'after'; limit: IsoDate };

export interface SegmentInputProps {
  value: IsoDate | null;
  /** A complete, in-bounds day — or `null` when the text was cleared. */
  onCommit: (value: IsoDate | null) => void;
  /** Publish a typed date equal to `value` too: the commit itself is the act. */
  commitUnchanged?: boolean;
  /**
   * Whether emptying the text clears the value (default). `false` — a required date — puts
   * the value back instead, as the MUI picker these fields replaced never cleared.
   */
  allowEmpty?: boolean;
  /**
   * Every keystroke that leaves a whole day typed — its year in full, so not `01.01.20` on
   * the way to `01.01.2024` — so the open calendar can follow it.
   */
  onTypedDay?: (value: IsoDate) => void;
  /**
   * Reports why the text is currently unusable (or `null`), so the field can say so —
   * SYNCHRONOUSLY, in the same event, so a blur that ends the edit already knows.
   */
  onProblemChange?: (problem: TypedProblem | null) => void;
  /** Bumped by the field when its value was set again (a pick, Escape): the text follows even if the day is the same. */
  revision?: number;
  /** Enter, once the text was taken (or there was nothing to take). */
  onEnter?: () => void;
  /**
   * The word an open end is typed and shown as ("open"; also English "open" in any UI
   * language). Set, the input accepts it as well as a date, and `open` shows it.
   */
  openWord?: string;
  /** The value is an open end: the input shows `openWord`. */
  open?: boolean;
  /** The open word was typed and entered. */
  onCommitOpen?: () => void;
  minDate?: IsoDate | null;
  maxDate?: IsoDate | null;
  size: FieldSize;
  grow?: boolean;
  placeholder?: string;
  'aria-label': string;
  'aria-describedby'?: string;
  /** Hover text — a dense field's refusal reason, which has no room under it. */
  title?: string;
  disabled?: boolean;
  readOnly?: boolean;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onMouseDown?: (event: MouseEvent<HTMLInputElement>) => void;
  /** Whether the calendar this input opens is open. Set, it makes the input a `combobox`. */
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  'data-testid'?: string;
}

const isOpenText = (text: string, word: string | undefined): boolean => {
  if (!word) return false;
  const typed = text.trim().toLowerCase();
  return typed === word.toLowerCase() || typed === 'open';
};

/** On the way to the open word: letters only, the start of either spelling. */
const isOpenPrefix = (text: string, word: string | undefined): boolean => {
  if (!word || !/^[a-z]+$/i.test(text)) return false;
  const typed = text.toLowerCase();
  return word.toLowerCase().startsWith(typed) || 'open'.startsWith(typed);
};

/**
 * One typed date — `DD.MM.YYYY`, its separators written as the digits arrive
 * (`maskTypedDate`), committed on blur or Enter.
 *
 * NOTHING IS PUBLISHED MID-TYPING: `01.0` is on its way to a date, not a date, and a live
 * filter would re-query on every character. A finished entry that names no real day (or
 * falls outside the bounds) keeps its text, turns red and publishes nothing — the value
 * stays the last good one, and Escape puts its text back.
 *
 * THE TEXT IS SELECTED ON FOCUS (spec *Typing dates*), so typing replaces it — also when
 * the field is clicked into: the click that focuses it does not collapse the selection.
 */
export const SegmentInput = forwardRef<HTMLInputElement, SegmentInputProps>(function SegmentInput(
  {
    value,
    onCommit,
    commitUnchanged = false,
    allowEmpty = true,
    onTypedDay,
    onProblemChange,
    revision = 0,
    onEnter,
    openWord,
    open = false,
    onCommitOpen,
    minDate,
    maxDate,
    size,
    grow,
    placeholder,
    disabled,
    readOnly,
    onFocus,
    onKeyDown,
    onMouseDown,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    title,
    'aria-expanded': ariaExpanded,
    'aria-controls': ariaControls,
    'data-testid': testId,
  },
  ref,
) {
  const { t } = useTranslation('dashboard', { keyPrefix: 'global' });
  const shownText = open && openWord ? openWord : formatIsoDate(value);
  const [text, setText] = useState(shownText);
  const [problem, setProblemState] = useState<TypedProblem | null>(null);
  const onProblemChangeRef = useRef(onProblemChange);
  useEffect(() => {
    onProblemChangeRef.current = onProblemChange;
  });
  const setProblem = (next: TypedProblem | null) => {
    setProblemState(next);
    onProblemChangeRef.current?.(next);
  };
  /**
   * Typed since the value last changed? ONLY THEN does a blur commit. A calendar pick
   * moves focus into the grid (the engine focuses the clicked day), and the blur that
   * causes lands before this input has re-rendered its text — committing that stale text
   * would publish the OLD date straight over the one just picked. A ref, because the blur
   * handler must see the reset in the same tick the value changed.
   */
  const dirtyRef = useRef(false);
  /** A press that focuses the input: its mouseup must not collapse the select-on-focus. */
  const focusingPress = useRef(false);

  // The value is the truth: a pick in the calendar, a preset or a server refresh rewrites
  // the text, and clears a complaint about text that is no longer there.
  useEffect(() => {
    dirtyRef.current = false;
    setText(shownText);
    setProblemState(null);
    onProblemChangeRef.current?.(null);
  }, [shownText, revision]);

  /** Takes the text; `false` when it was refused (and stays red). */
  const commit = (): boolean => {
    if (!dirtyRef.current) return problem === null;
    if (isOpenText(text, openWord)) {
      dirtyRef.current = false;
      setProblem(null);
      setText(openWord ?? text);
      if (!open || commitUnchanged) onCommitOpen?.();
      return true;
    }
    const typed = parseTypedDate(text);
    if (typed.kind === 'empty') {
      dirtyRef.current = false;
      setProblem(null);
      if (!allowEmpty) {
        setText(shownText);
        return true;
      }
      if (value !== null || open) onCommit(null);
      return true;
    }
    if (typed.kind === 'invalid') {
      // Stays dirty: the next blur or Enter checks the text again.
      setProblem({ kind: 'invalid' });
      return false;
    }
    if (!isWithinBounds(typed.iso, minDate, maxDate)) {
      setProblem(
        minDate && typed.iso < minDate
          ? { kind: 'before', limit: minDate }
          : { kind: 'after', limit: maxDate as IsoDate },
      );
      return false;
    }
    dirtyRef.current = false;
    setProblem(null);
    setText(formatIsoDate(typed.iso));
    if (commitUnchanged || typed.iso !== value || open) onCommit(typed.iso);
    return true;
  };

  return (
    <SegmentInputElement
      ref={ref}
      type='text'
      inputMode={openWord ? 'text' : 'numeric'}
      autoComplete='off'
      fieldSize={size}
      grow={grow}
      value={text}
      placeholder={placeholder ?? String(t('calendar_date_placeholder'))}
      aria-label={ariaLabel}
      aria-invalid={problem !== null || undefined}
      aria-describedby={ariaDescribedBy}
      title={title}
      /* A field that opens a calendar is a combobox: the WAI-ARIA date-picker combobox
         (`aria-haspopup="dialog"`, `aria-expanded`, `aria-controls` while open). A plain
         textbox may not carry `aria-expanded` (axe `aria-allowed-attr`), and on a field
         drawn without its glyph the input is the only control that opens the calendar. */
      role={ariaExpanded === undefined ? undefined : 'combobox'}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      aria-haspopup={ariaExpanded === undefined ? undefined : 'dialog'}
      disabled={disabled}
      readOnly={readOnly}
      data-testid={testId}
      onChange={(event) => {
        dirtyRef.current = true;
        const { value: raw, selectionStart } = event.target;
        const { isComposing, inputType } = event.nativeEvent as Partial<InputEvent>;
        // An IME builds its text in steps; the mask leaves the steps alone, and so it does
        // the open word on its way.
        const masked =
          isComposing || isOpenPrefix(raw, openWord)
            ? raw
            : maskTypedDate({
                previous: text,
                next: raw,
                caret: selectionStart ?? raw.length,
                inputType,
              });
        setText(masked);
        if (problem) setProblem(null);
        const day = parseCompleteTypedDate(masked);
        if (day && isWithinBounds(day, minDate, maxDate)) onTypedDay?.(day);
      }}
      onBlur={() => {
        commit();
      }}
      onFocus={(event) => {
        if (!readOnly) event.target.select();
        onFocus?.(event);
      }}
      onMouseDown={(event) => {
        if (document.activeElement !== event.currentTarget) focusingPress.current = true;
        onMouseDown?.(event);
      }}
      onMouseUp={(event) => {
        if (!focusingPress.current) return;
        focusingPress.current = false;
        event.preventDefault();
      }}
      onKeyDown={(event) => {
        /* A text field's keys are its own. Inside a Menu they would otherwise reach the
           list's arrow navigation and its type-ahead — a typed "0" jumps focus to the
           first item starting with "0", and the field loses the date half-typed. Tab
           and Escape still travel: they belong to the page and to the Menu. */
        if (event.key !== 'Tab' && event.key !== 'Escape') event.stopPropagation();
        if (event.key === 'Enter') {
          event.preventDefault();
          if (commit()) onEnter?.();
          return;
        }
        if (event.key === 'Escape' && (dirtyRef.current || text !== shownText)) {
          // Escape puts the text back; the field then decides what else it closes. With
          // nothing else to close it stops here, so a surrounding Menu stays open.
          dirtyRef.current = false;
          setText(shownText);
          setProblem(null);
          onKeyDown?.(event);
          event.stopPropagation();
          return;
        }
        onKeyDown?.(event);
      }}
    />
  );
});

const MessageText = styled('p')(({ theme }) => ({
  margin: '4px 0 0',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.4,
  color: theme.palette.error.main,
}));

/**
 * A refused entry's reason, under the field (spec *Validation*: errors show inline). A
 * `small` field sits in a dense row or a table cell, where a line under it would move
 * the row, so there the reason is only announced, through the input's description.
 */
export function FieldMessage({
  id,
  message,
  visible,
}: {
  id: string;
  message: string | null;
  visible: boolean;
}) {
  if (!message) return null;
  return (
    <MessageText id={id} role='alert' style={visible ? undefined : visuallyHidden}>
      {message}
    </MessageText>
  );
}

/** Figma's calendar slot: the glyph in #A0B7BA, darkening under the pointer. */
const ToggleButton = styled(IconButton)(({ theme }) => ({
  padding: 4,
  borderRadius: 6,
  color: theme.palette.grey[80],
  '&:hover': { backgroundColor: theme.palette.grey[100], color: theme.palette.text.tertiary },
}));

/** The calendar glyph — the Figma `input`'s visible calendar slot. Opens the grid. */
export function CalendarToggle({
  open,
  onToggle,
  disabled,
  size,
  controls,
  testId,
}: {
  open: boolean;
  onToggle: () => void;
  disabled?: boolean;
  size: FieldSize;
  controls?: string;
  testId?: string;
}) {
  const { t } = useTranslation('dashboard', { keyPrefix: 'global' });
  return (
    <ToggleButton
      size='small'
      disableRipple
      disabled={disabled}
      aria-label={String(t('calendar_open'))}
      aria-haspopup='dialog'
      aria-expanded={open}
      aria-controls={open ? controls : undefined}
      // Keep focus where it is (usually the input being typed in).
      onMouseDown={(event) => event.preventDefault()}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      data-testid={testId}
      sx={size === 'small' ? { padding: '2px' } : undefined}
    >
      <Calendar size={size === 'small' ? 14 : 16} aria-hidden />
    </ToggleButton>
  );
}

/**
 * A field and the line under it: the shell, and the reason a typed entry was refused. The
 * calendar anchors to this box, so it opens below the message rather than over it.
 */
export const FieldRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  display: fullWidth ? 'flex' : 'inline-flex',
  flexDirection: 'column',
  width: fullWidth ? '100%' : undefined,
  minWidth: 0,
}));

type Translate = (key: string, options?: Record<string, string>) => unknown;

/**
 * Why typed text was refused, in the spec's words (*Validation*): not a real day, an end
 * before the start (or a start after the end), or a day outside the allowed dates.
 */
export function describeProblem(
  t: Translate,
  problem: TypedProblem | null,
  ends?: { bound: 'start' | 'end'; start: IsoDate | null; end: IsoDate | null },
): string | null {
  if (!problem) return null;
  if (problem.kind === 'invalid') return String(t('calendar_invalid_date'));
  if (ends?.bound === 'end' && problem.kind === 'before' && problem.limit === ends.start) {
    return String(t('calendar_error_end_before_start'));
  }
  if (ends?.bound === 'start' && problem.kind === 'after' && problem.limit === ends.end) {
    return String(t('calendar_error_start_after_end'));
  }
  return String(
    t(problem.kind === 'before' ? 'calendar_error_before_min' : 'calendar_error_after_max', {
      date: formatIsoDate(problem.limit),
    }),
  );
}
